import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import { shallowEqual } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setOrderBy, setToastMessage } from '@slices/commentSlice';
import { useQuery } from '@apollo/client';
import { LIST_CHAPTER_COMMENT } from '@operations/queries/comment/useListChapterComment';
import { ChapterComment, ListChapterCommentQuery } from '@customTypes/comment';
import { css } from '@emotion/react';
import { NextBtn } from 'public/assets';
import theme from '@styles/theme';
import { formatDate } from '@common/utils';
import { SORT_BY } from '@common/values';

import BestLabel from '@components/comment/common/BestLabel';
import { Story_Play_Type } from '@/baseType';

const CommentTitle = ({ commentCount }: { commentCount: number }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { playChapterInfo } = useAppSelector(
    (state) => state.chapter,
    shallowEqual,
  );
  const onComment = () => {
    if (playChapterInfo?.currentPlayInfo) {
      dispatch(setOrderBy(SORT_BY.best));
      dispatch(setToastMessage(t('common_toast_title_show_best')));
      router.push({
        pathname: '/comment',
        query: {
          storyId: playChapterInfo?.currentPlayInfo?.storyId,
          chapterId: playChapterInfo?.currentPlayInfo?.chapterId,
          chapterIndex: playChapterInfo?.currentPlayInfo?.chapterIndex,
          url: `/story/${playChapterInfo.currentPlayInfo.storyId}`,
          playType: Story_Play_Type.Interactive,
        },
      });
    }
  };

  return (
    <div css={title}>
      <div className="title-container">
        <div className="title">{t('common_screen_title_comment')}</div>
        <div className="count">{commentCount || 0}</div>
      </div>
      <div className="button" onClick={onComment}>
        <NextBtn width={16} height={16} stroke={theme.colors.gray800} />
      </div>
    </div>
  );
};

const CommentEmpty = () => {
  const { t } = useTranslation();
  return <div css={empty}>{t('common_screen_description_empty_comment')}</div>;
};

const ChatCommentItem = ({ comment }: { comment: ChapterComment }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { playChapterInfo } = useAppSelector(
    (state) => state.chapter,
    shallowEqual,
  );

  const onComment = () => {
    if (playChapterInfo?.currentPlayInfo) {
      dispatch(setOrderBy(SORT_BY.best));
      dispatch(setToastMessage(t('common_toast_title_show_best')));
      router.push({
        pathname: '/comment',
        query: {
          storyId: playChapterInfo?.currentPlayInfo?.storyId,
          chapterId: playChapterInfo?.currentPlayInfo?.chapterId,
          chapterIndex: playChapterInfo?.currentPlayInfo?.chapterIndex,
          url: `/story/${playChapterInfo?.currentPlayInfo?.storyId}`,
        },
      });
    }
  };

  return (
    <div css={item} onClick={onComment}>
      <div className="line" />
      <div className="item">
        <div className="comment-info">
          <div className="profile">
            {comment.isBestComment && <BestLabel />}
            <p className="user-name">{comment.author?.name}</p>
          </div>
          <p className="date">{formatDate(new Date(comment.createdAt), '.')}</p>
        </div>
        <div className="comment-content">
          <p className="content">
            {comment.isSpoiler ? (
              <span className="spoiler">
                {t('comment_screen_button_contain_spoiler_comment')}
              </span>
            ) : (
              comment.content
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

const ChatTopComment = ({ list }: { list: ChapterComment[] }) => {
  return (
    <div css={bestList}>
      {list.map((comment) => (
        <ChatCommentItem key={comment.ccId} comment={comment} />
      ))}
    </div>
  );
};

const ChatEndingComment = ({ reload }: { reload?: boolean }) => {
  const { playChapterInfo } = useAppSelector(
    (state) => state.chapter,
    shallowEqual,
  );
  const isStoryGame = useRouter().pathname.includes('storygame');

  const { data: bestComment, refetch } = useQuery<ListChapterCommentQuery>(
    LIST_CHAPTER_COMMENT,
    {
      variables: {
        storyId: playChapterInfo?.playChapter.storyId,
        chapterId: playChapterInfo?.playChapter.chapterId,
        page: 1,
        pageSize: 2,
        orderBy: {
          field: 'UserLikes',
          direction: 'DESC',
        },
        exceptHidden: true,
        exceptDeleted: true,
      },
    },
  );

  useEffect(() => {
    if (reload !== undefined) {
      refetch();
    }
  }, [reload, refetch]);

  if (!bestComment) {
    return null;
  }

  return (
    <div css={container({ isStoryGame })}>
      <CommentTitle
        commentCount={bestComment.listChapterCommentV3.totalCount}
      />
      {bestComment.listChapterCommentV3.totalCount > 0 ? (
        <ChatTopComment list={bestComment.listChapterCommentV3.list} />
      ) : (
        <CommentEmpty />
      )}
    </div>
  );
};

const container = ({ isStoryGame }: { isStoryGame: boolean }) => css`
  margin: 0.75rem 1rem;
  border: none;
  border-radius: 0.5em;
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  padding: 1.5em 0;

  ${isStoryGame &&
  theme.setTabletStyle(css`
    margin: 0;
  `)}
`;

const title = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin: 0 1.5em;

  .title-container {
    display: flex;
    flex-direction: row;
  }

  .title {
    display: flex;
    align-items: center;
    font: ${theme.font.header.headline1};
    color: ${theme.colors.gray900};
  }
  .count {
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
    font-weight: 400;
    font-size: 1.25rem;
    line-height: 1.5rem;
    color: ${theme.colors.gray500};
  }

  .button {
    display: flex;
    justify-content: flex-end;
    width: 2em;
    cursor: pointer;
  }
`;

const empty = css`
  margin: 1.25em 0;
  text-align: center;
  width: 100%;
  font: ${theme.font.body.body1};
  color: ${theme.colors.gray500};
  white-space: pre-line;
`;

const item = css`
  cursor: pointer;
  .line {
    height: 1px;
    background-color: ${theme.colors.gray200};
  }

  .item {
    margin: 0 1.5em;

    .comment-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.5rem;

      .profile {
        display: flex;

        .user-name {
          ${theme.font.header.subhead1}
          color: ${theme.colors.gray900};
        }
      }

      .date {
        ${theme.font.body.caption}
        color: ${theme.colors.gray500};
      }
    }

    .comment-content {
      display: flex;
      flex-direction: column;

      .content {
        ${theme.font.body.body1}
        color: ${theme.colors.gray700};

        .spoiler {
          color: ${theme.colors.red500};
          cursor: pointer;
        }
      }
    }

    padding: 1em 0;
  }
`;

const bestList = css`
  margin: 1.25rem 0;
  display: flex;
  flex-direction: column;
  gap: 1em;

  .line {
    height: 2px;
    background-color: ${theme.colors.gray200};
  }
`;

export default ChatEndingComment;
